import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import {
  Mail, Phone, Flag, User, Calendar, BadgeCheck,
  Briefcase, MapPin, BookOpen, FileText, Upload,
  ChevronDown, ChevronUp, Pencil, Check, X, Clock,
  Lock, ShieldAlert, EyeOff, Heart, DollarSign,
} from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00ff90";

const card =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] hover:shadow-[0_0_20px_#00ff9044] transition-shadow";

// ─── Access control helpers ───────────────────────────────────────────────────
//
// Roles:  "admin" | "superadmin" | "manager" | "staff"
// viewingSelf: true when user is viewing their own profile
//
// Field visibility/editability rules:
//
// PRIVATE fields (phone, address, salary):
//   - Hidden entirely from managers viewing another user's profile
//   - Visible to the user themselves and admins
//
// ADMIN-ONLY editable fields (role, dept, manager, start date, band, location,
//                              employee_id, work email, work phone):
//   - Everyone can SEE them
//   - Only admins can EDIT them
//
// SELF-EDITABLE fields (nationality, gender, age, personal email, personal phone):
//   - User can edit their own; admins can edit anyone's
//
// ─────────────────────────────────────────────────────────────────────────────

function useAccess() {
  const { userId } = useParams();
  const viewingSelf = !userId;
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const role = currentUser?.role || "staff";
  const isAdmin = role === "admin" || role === "superadmin";
  const isManager = role === "manager";

  return {
    viewingSelf,
    isAdmin,
    isManager,
    userId,
    // Can this viewer edit admin-only fields?
    canEditAdminFields: isAdmin,
    // Can this viewer edit self-editable fields?
    canEditSelfFields: isAdmin || viewingSelf,
    // Should private sections be visible at all?
    canViewPrivate: isAdmin || viewingSelf,
    // Can upload documents?
    canUploadDocs: isAdmin || viewingSelf,
  };
}

// ─── Field component ──────────────────────────────────────────────────────────
// mode: "admin-edit" | "self-edit" | "view-only" | "hidden"
function Field({ label, fieldKey, value, mode, userId, onSaved, icon }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  if (mode === "hidden") return null;

  const editable = mode === "admin-edit" || mode === "self-edit";

  const start = () => {
    if (!editable) return;
    setDraft(value ?? "");
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const endpoint = userId
        ? `${API_BASE}/api/profile/${userId}/update-field`
        : `${API_BASE}/api/profile/update-field`;
      const res = await fetch(
        `${endpoint}?field_name=${fieldKey}&new_value=${encodeURIComponent(draft)}`,
        { method: "PATCH", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Save failed");
      setEditing(false);
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error("Failed to save: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
      {icon && <span className="mt-0.5 text-[#00ff90]/40 shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{label}</p>
          {mode === "view-only" && (
            <Lock size={9} className="text-white/20" title="Admin only" />
          )}
        </div>

        {editing ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
              className="flex-1 bg-black border border-[#00ff90]/40 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/80 transition-colors"
              disabled={saving}
            />
            <button onClick={save} disabled={saving} className="text-[#00ff90] hover:text-[#00ff90]/70 transition-colors">
              <Check size={15} />
            </button>
            <button onClick={cancel} className="text-white/30 hover:text-white/60 transition-colors">
              <X size={15} />
            </button>
          </div>
        ) : (
          <div
            onClick={start}
            className={`text-sm text-white flex items-center gap-2 ${editable ? "cursor-pointer group-hover:text-[#00ff90] transition-colors" : ""}`}
          >
            <span className="truncate">{value || <span className="text-white/20 italic">Not set</span>}</span>
            {editable && (
              <Pencil size={11} className="opacity-0 group-hover:opacity-40 shrink-0 transition-opacity" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Private section wrapper ──────────────────────────────────────────────────
// Shows a "restricted" banner instead of content when viewer lacks access
function PrivateSection({ title, icon, canView, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  if (!canView) {
    return (
      <div className="rounded-xl border border-white/5 bg-[#0d0d0d] opacity-60">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <EyeOff size={14} className="text-white/20" />
            <span className="text-xs text-white/20 uppercase tracking-widest font-medium">{title}</span>
          </div>
          <span className="flex items-center gap-1 text-xs text-white/20 border border-white/10 px-2 py-0.5 rounded-full">
            <Lock size={10} /> Private
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={card}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-2">
          <span className="text-[#00ff90]/60">{icon}</span>
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">{title}</span>
          <span className="flex items-center gap-1 text-xs text-amber-400/60 border border-amber-400/20 bg-amber-400/5 px-2 py-0.5 rounded-full">
            <ShieldAlert size={9} /> Private
          </span>
        </div>
        <span className="text-white/20">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ─── Regular collapsible section ─────────────────────────────────────────────
function Section({ title, icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={card}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-2">
          <span className="text-[#00ff90]/60">{icon}</span>
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">{title}</span>
          {badge}
        </div>
        <span className="text-white/20">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ─── Profile card (left column top) ──────────────────────────────────────────
function ProfileCard({ data, canEditAvatar, onAvatarUpdated }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/profile/upload-avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Profile photo updated");
      onAvatarUpdated?.();
    } catch {
      toast.error("Could not upload photo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={`${card} overflow-hidden`}>
      {/* Neon header bar */}
      <div className="h-20 w-full bg-gradient-to-r from-[#00ff90]/20 via-[#00ff90]/5 to-transparent relative">
        <div className="absolute inset-0" style={{
          backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,255,144,0.03) 40px, rgba(0,255,144,0.03) 41px)",
        }} />
      </div>

      {/* Avatar centred, overlapping header */}
      <div className="flex flex-col items-center -mt-10 pb-5 px-5">
        <div style={{ position: "relative", display: "inline-block" }}>
          <div className="w-20 h-20 rounded-2xl ring-4 ring-[#0d0d0d] overflow-hidden bg-black border border-[#00ff90]/30 shadow-[0_0_20px_#00ff9033]">
            {data.avatar_url ? (
              <img src={data.avatar_url} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-[#00ff90] text-2xl font-bold">
                {String(data.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>

          {canEditAvatar && (
            <>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                aria-label="Upload profile photo"
                title="Upload profile photo"
                style={{
                  position:       "absolute",
                  bottom:         -2,
                  right:          -2,
                  width:          26,
                  height:         26,
                  borderRadius:   "50%",
                  background:     NEON,
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  cursor:         uploading ? "wait" : "pointer",
                  fontSize:       12,
                  color:          "#030303",
                  border:         "2px solid #0d0d0d",
                  opacity:        uploading ? 0.6 : 1,
                }}
              >
                {uploading ? "…" : <Pencil size={11} />}
              </button>
              <input
                type="file"
                ref={fileRef}
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarUpload}
              />
            </>
          )}
        </div>

        <h1 className="mt-3 text-lg font-bold text-white text-center truncate w-full">{data.name || "—"}</h1>

        {data.employee_code && (
          <p className="text-xs text-white/30 mt-0.5">#{data.employee_code}</p>
        )}

        <div className="flex items-center gap-2 mt-3 flex-wrap justify-center">
          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border"
            style={{ color: NEON, background: "rgba(0,255,144,0.08)", borderColor: "rgba(0,255,144,0.25)" }}>
            <BadgeCheck size={12} /> {data.role || "Staff"}
          </span>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${
            data.status === "Active"
              ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
              : "text-white/30 bg-white/5 border-white/10"
          }`}>{data.status || "Active"}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Document section ─────────────────────────────────────────────────────────
function DocumentSection({ token, canUpload }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const pendingDocs = [
    { name: "IT Policy Acknowledgement", due: "15 Jun 2025", action: "Acknowledge" },
    { name: "Health Declaration Form", due: "20 Jun 2025", action: "Fill Form" },
  ];

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/api/staffprofile/upload-document/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      setUploaded(true);
      setFile(null);
      toast.success("Document uploaded ✓");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Pending Actions</p>
        <div className="space-y-2">
          {pendingDocs.map((doc, i) => (
            <div key={i} className="flex items-center justify-between gap-3 bg-[#00ff90]/5 border border-[#00ff90]/15 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm text-white">{doc.name}</p>
                <p className="text-xs text-white/30 mt-0.5">Due: {doc.due}</p>
              </div>
              <button className="text-xs px-3 py-1.5 rounded-full bg-[#00ff90]/10 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/20 transition-colors shrink-0">
                {doc.action}
              </button>
            </div>
          ))}
        </div>
      </div>

      {canUpload && (
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Upload Document</p>
          <div className="border border-dashed border-[#00ff90]/20 rounded-xl p-5 flex items-center gap-4 bg-[#00ff90]/5">
            <Upload size={20} className="text-[#00ff90]/40 shrink-0" />
            <div className="flex-1 min-w-0">
              <input type="file" onChange={e => { setFile(e.target.files[0]); setUploaded(false); }} className="text-sm text-white/60 w-full" />
              {file && <p className="text-xs text-white/30 mt-1 truncate">{file.name}</p>}
            </div>
            <button onClick={handleUpload} disabled={!file || uploading}
              className="text-xs px-4 py-2 rounded-full bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors shrink-0">
              {uploading ? "Uploading…" : uploaded ? "✓ Uploaded" : "Upload"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Audit trail ──────────────────────────────────────────────────────────────
function AuditTrail({ userId, reloadKey }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const endpoint = userId
      ? `${API_BASE}/api/profile/${userId}/audit`
      : `${API_BASE}/api/profile/audit`;
    fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setLogs(Array.isArray(d.data) ? d.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, reloadKey]);

  if (loading) return <p className="text-white/20 text-sm animate-pulse">Loading history…</p>;
  if (!logs.length) return <p className="text-white/20 text-sm">No recent changes.</p>;

  return (
    <ul className="space-y-2">
      {logs.map((e, i) => (
        <li key={i} className="flex items-start gap-3 bg-black/40 rounded-lg px-4 py-3">
          <Clock size={13} className="text-[#00ff90]/40 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[#00ff90]/60">{e.date ? new Date(e.date).toLocaleString() : "—"}</span>
              <span className="text-xs text-white/30">by {e.changed_by || "system"}</span>
            </div>
            <p className="text-sm text-white/70 mt-0.5">
              <span className="text-white/50">{e.field}</span>{": "}
              <span className="line-through text-white/30">{e.old ?? "—"}</span>
              {" → "}
              <span className="text-[#00ff90]">{e.new ?? "—"}</span>
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Qualifications ───────────────────────────────────────────────────────────
function Qualifications({ list, editable, onAdd }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="space-y-3">
      {list.length > 0 ? (
        <ul className="space-y-2">
          {list.map((q, i) => {
            const text = typeof q === "string" ? q : [q.title || q.name, q.year ? `(${q.year})` : ""].filter(Boolean).join(" ");
            return (
              <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00ff90]/50 shrink-0" />{text}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-white/20 text-sm italic">No qualifications added yet.</p>
      )}
      {editable && (
        <div className="flex items-center gap-2 mt-3">
          <input value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && draft.trim()) { onAdd(draft.trim()); setDraft(""); } }}
            placeholder="e.g. BSc Nursing 2020 — press Enter"
            className="flex-1 bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 outline-none focus:border-[#00ff90]/50 transition-colors"
          />
          <button onClick={() => { if (draft.trim()) { onAdd(draft.trim()); setDraft(""); } }}
            className="text-xs px-4 py-2 rounded-lg bg-[#00ff90]/10 text-[#00ff90] border border-[#00ff90]/25 hover:bg-[#00ff90]/20 transition-colors">
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ProfileMessengerPage() {
  const { userId } = useParams();
  const viewingSelf = !userId;

  const [profileData, setProfileData] = useState(null);
  const [qualifications, setQualifications] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  const token = localStorage.getItem("token") || "";
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const role = currentUser?.role || "staff";
  const isAdmin = role === "admin" || role === "superadmin";
  const isManager = role === "manager";

  // Access rules
  const canEditAdminFields = isAdmin;
  const canEditSelfFields = isAdmin || viewingSelf;
  const canViewPrivate = isAdmin || viewingSelf;     // phone, address, docs
  const canViewPersonal = isAdmin || viewingSelf;    // gender, nationality, age — hidden from managers
  const canUploadDocs = isAdmin || viewingSelf;

  // Field mode helpers
  const adminEdit        = canEditAdminFields ? "admin-edit" : "view-only";
  const selfEdit         = (isAdmin || viewingSelf) ? (isAdmin ? "admin-edit" : "self-edit") : "view-only";
  const privateAdminEdit = canViewPrivate  ? (canEditAdminFields ? "admin-edit" : "view-only") : "hidden";
  const privateSelfEdit  = canViewPrivate  ? (canEditSelfFields  ? "self-edit"  : "view-only") : "hidden";
  // Personal fields: visible only to self+admin, editable by admin only
  const personalAdminEdit = canViewPersonal ? (canEditAdminFields ? "admin-edit" : "view-only") : "hidden";

  const loadProfile = async () => {
    try {
      setStatus("loading");
      const endpoint = viewingSelf
        ? `${API_BASE}/api/profile/me`
        : `${API_BASE}/api/profile/${userId}`;
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load profile (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setProfileData(data);
      setQualifications(Array.isArray(data?.qualifications) ? data.qualifications : []);
      setStatus("success");
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  };

  useEffect(() => {
    loadProfile();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["profile_update", "emergency_contact_update", "bank_update"].includes(t)) {
        loadProfile();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (status === "loading") {
    return (
      <MessengerLayout>
        <div className="p-8 space-y-4">
          {[1, 2, 3].map(i => <div key={i} className={`${card} h-24 animate-pulse`} />)}
        </div>
      </MessengerLayout>
    );
  }

  if (status === "error") {
    return (
      <MessengerLayout>
        <div className="p-8">
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-red-300 text-sm">{error}</div>
        </div>
      </MessengerLayout>
    );
  }

  const d = profileData || {};
  const F = (props) => <Field {...props} userId={userId} onSaved={loadProfile} />;

  return (
    <MessengerLayout>
      <div className="p-6 space-y-5">

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-5">

          {/* LEFT column */}
          <div className="space-y-5">

            {/* Profile card — avatar, name, role, status */}
            <ProfileCard
              data={d}
              canEditAvatar={viewingSelf}
              onAvatarUpdated={loadProfile}
            />

            {/* Personal info — nationality/gender/age hidden from managers, admin-only edit */}
            <Section title="Personal Info" icon={<User size={14} />}>
              <F label="Nationality"   fieldKey="nationality"   value={d.nationality}   mode={personalAdminEdit} icon={<Flag size={14} />} />
              <F label="Gender"        fieldKey="gender"        value={d.gender}        mode={personalAdminEdit} icon={<User size={14} />} />
              <F label="Age"           fieldKey="age"           value={d.age}           mode={personalAdminEdit} icon={<Calendar size={14} />} />
              <F label="Date of Birth" fieldKey="date_of_birth" value={d.date_of_birth} mode={personalAdminEdit} icon={<Calendar size={14} />} />
            </Section>

            {/* Private contact — hidden from managers */}
            <PrivateSection title="Private Contact" icon={<Phone size={14} />} canView={canViewPrivate}>
              <F label="Personal Email" fieldKey="personal_email" value={d.personal_email} mode={privateSelfEdit} icon={<Mail size={14} />} />
              <F label="Personal Phone" fieldKey="phone"          value={d.phone}          mode={privateSelfEdit} icon={<Phone size={14} />} />
            </PrivateSection>

            {/* Home address — PRIVATE */}
            <PrivateSection title="Home Address" icon={<MapPin size={14} />} canView={canViewPrivate}>
              <F label="Address"  fieldKey="address"     value={d.address}     mode={privateSelfEdit} />
              <F label="City"     fieldKey="city"        value={d.city}        mode={privateSelfEdit} />
              <F label="State"    fieldKey="state"       value={d.state}       mode={privateSelfEdit} />
              <F label="Postcode" fieldKey="postal_code" value={d.postal_code} mode={privateSelfEdit} />
              <F label="Country"  fieldKey="country"     value={d.country}     mode={privateSelfEdit} />
            </PrivateSection>

            {/* Emergency contact — PRIVATE, hidden from managers */}
            <PrivateSection title="Emergency Contact" icon={<Heart size={14} />} canView={canViewPrivate}>
              <F label="Contact Name"  fieldKey="emergency_contact_name"         value={d.emergency_contact_name}         mode={privateSelfEdit} icon={<User size={14} />} />
              <F label="Contact Phone" fieldKey="emergency_contact_phone"        value={d.emergency_contact_phone}        mode={privateSelfEdit} icon={<Phone size={14} />} />
              <F label="Relationship"  fieldKey="emergency_contact_relationship" value={d.emergency_contact_relationship} mode={privateSelfEdit} />
            </PrivateSection>

          </div>

          {/* RIGHT column */}
          <div className="space-y-5">

            {/* Job & Employment — all admin-only editable */}
            <Section title="Job & Employment" icon={<Briefcase size={14} />}
              badge={
                <span className="flex items-center gap-1 text-xs text-white/25 border border-white/10 px-2 py-0.5 rounded-full ml-1">
                  <Lock size={9} /> Admin editable
                </span>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <F label="Role"               fieldKey="role"               value={d.role}               mode={adminEdit} />
                <F label="Department"         fieldKey="department"         value={d.department}         mode={adminEdit} />
                <F label="Band"               fieldKey="band"               value={d.band}               mode={adminEdit} />
                <F label="Manager"            fieldKey="manager"            value={d.manager}            mode={adminEdit} />
                <F label="Employment Type"    fieldKey="employment_type"    value={d.employment_type}    mode={adminEdit} />
                <F label="Start Date"         fieldKey="start_date"         value={d.start_date}         mode={adminEdit} icon={<Calendar size={14} />} />
                <F label="Probation End Date" fieldKey="probation_end_date" value={d.probation_end_date} mode={adminEdit} icon={<Calendar size={14} />} />
                <F label="End Date"           fieldKey="end_date"           value={d.end_date}           mode={adminEdit} icon={<Calendar size={14} />} />
                <F label="Location"           fieldKey="location"           value={d.location}           mode={adminEdit} />
                <F label="Employee ID"        fieldKey="employee_code"      value={d.employee_code}      mode={adminEdit} />
                <F label="Currency"           fieldKey="currency"           value={d.currency}           mode={adminEdit} icon={<DollarSign size={14} />} />
              </div>
              {/* Work contact — visible to all, admin-only edit */}
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Work Contact</p>
                <F label="Work Email" fieldKey="email"      value={d.email}      mode={adminEdit} icon={<Mail size={14} />} />
                <F label="Work Phone" fieldKey="work_phone" value={d.work_phone} mode={adminEdit} icon={<Phone size={14} />} />
              </div>
            </Section>

            {/* Professional background — self-editable */}
            <Section title="Professional Background" icon={<BookOpen size={14} />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <F label="Education Level" fieldKey="education_level" value={d.education_level} mode={selfEdit} />
                <F label="Degree"          fieldKey="degree"          value={d.degree}          mode={selfEdit} />
                <F label="Hard Skills"     fieldKey="hard_skill"      value={d.hard_skill}      mode={selfEdit} />
                <F label="Soft Skills"     fieldKey="soft_skill"      value={d.soft_skill}      mode={selfEdit} />
              </div>
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Qualifications & Certifications</p>
                <Qualifications
                  list={qualifications}
                  editable={canEditSelfFields}
                  onAdd={q => setQualifications(prev => [...prev, q])}
                />
              </div>
            </Section>

            {/* Documents — private, user + admin only */}
            <PrivateSection title="Documents" icon={<FileText size={14} />} canView={canViewPrivate}>
              <DocumentSection token={token} canUpload={canUploadDocs} />
            </PrivateSection>

          </div>
        </div>

        {/* ── Change history — admin only ── */}
        {isAdmin && (
          <Section title="Change History" icon={<Clock size={14} />} defaultOpen={false}>
            <AuditTrail userId={userId} reloadKey={d.updated_at} />
          </Section>
        )}

      </div>
    </MessengerLayout>
  );
}